import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink, RouterLinkActive, RouterOutlet, Router } from '@angular/router';
import { ModalService } from '../../../../services/modal.service';
import { Observable, forkJoin, fromEvent, merge } from 'rxjs';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, share, map } from 'rxjs/operators';
import { ItemsPerPageComponent } from '../../../../components/itemsPerPage.component';
import { I18nTranslator } from '../../../../i18n/translate';
import { ARColzaCampaign, ARColzaOrderItem, ARColzaParcel } from '../../../../models/agrorendement-colza.model';
import { Campaign } from '../../../../models/campaign.model';
import { OrderItemDeletionFailure, OrderItemDeletionSummary } from '../../../../models/order-item-deletion.model';
import { ListItem, Variety } from '../../../../models/lists.model';
import { Organisation } from '../../../../models/organisation.model';
import { Page } from '../../../../models/page.model';
import { CampaignYearPipe } from '../../../../pipes/campaignYear.pipe';
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe';
import { ExtractAttributePipe } from '../../../../pipes/extractAttribute.pipe';
import { HectareConverterPipe } from '../../../../pipes/hectareConverter.pipe';
import { ToLabelPipe } from '../../../../pipes/toLabel.pipe';
import { ARColzaParcelFilter, ARColzaService } from '../../../../services/agrorendement-colza.service';
import { BeApiService } from '../../../../services/beapi.service';
import { KeycloakService } from '../../../../services/keycloak.service';
import { ListType, ListsService } from '../../../../services/lists.service';
import { OrganisationService } from '../../../../services/organisation.service';
import { FarmService } from '../../../../services/farm.service';
import { Farm } from '../../../../models/farm.model';
import { AutocompleteComponent, idsMatchingText } from '../../../../components/autocomplete/autocomplete.component';
import { Tooltip } from '../../../../utils/Tooltip';
import { TooltipContent } from '../../../../utils/TooltipContent';
import { TOOLTIP_TEXTS } from '../../../../utils/tooltip-texts';
import {
  currentCampaign,
  extractErrorMessage,
  showErrorMessage,
  toBoolean,
  toNull,
  toNumber,
} from '../../../../utils/utils';
import { CustomStatusModalComponent, StatusModalContext } from '../../../planning/custom-status-modal.component';
import {
  ParcelDetailsDialogComponent,
  ParcelDetailsDialogContext,
} from '../../../parcel/parcel-details-dialog.component';
import { ARColzaUtils } from './colza.utils';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

@Component({
  standalone: true,
  selector: 'ar-colza-parcels-cmp',
  templateUrl: 'parcels.component.html',
  styleUrls: ['parcels.component.scss'],
  imports: [
    AutocompleteComponent,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    CommonModule,
    FormsModule,
    Tooltip,
    TooltipContent,
    ItemsPerPageComponent,
    HectareConverterPipe,
    ToLabelPipe,
    CapitalizePipe,
    ExtractAttributePipe,
    CampaignYearPipe,
    TranslatePipe,
    PaginationModule,
    TooltipModule,
  ],
  providers: [ARColzaService, OrganisationService, BeApiService, FarmService],
})
export class ARColzaParcelComponent implements OnInit, AfterViewInit {
  error: any;
  @ViewChild('termPrefixFilterParcel') termPrefixFilterParcel: ElementRef;
  @ViewChild('termPrefixFilterIdParcel') termPrefixFilterIdParcel: ElementRef;
  @ViewChild('termCampaignFilter') termCampaignFilter: ElementRef;
  @ViewChild('termCompletionStatusFilter') termCompletionStatusFilter: ElementRef;
  @ViewChild('termOrderStatusFilter') termOrderStatusFilter: ElementRef;
  @ViewChild('allFilters', { static: true }) allFilters: ElementRef;
  @ViewChild('termVarietyFilter') termVarietyFilter: ElementRef;
  @ViewChild('termAdvisorFilter') termAdvisorFilter: ElementRef;

  // gestion des checkboxes
  @ViewChildren('parcelCheckbox') parcelCheckboxes: any;
  @ViewChild('checkboxAll') checkboxAll: any;
  lengthAllParcels: number;
  checkedParcelIds = new Set<number>();
  checkedOrderItemIds = new Set<number>();
  openedCampaignsDateChecks = new Map<number, any>();

  prefixFilterParcel: string = '';
  prefixFilterExploitation: string = '';
  prefixFilterIdParcel: number = null;
  campaignFilter: number = null;
  completionStatusFilter: boolean = null;
  orderStatusFilter: boolean = null;
  organisationFilter: number = null;
  organisationFilterText: string = '';
  // Farm picked in the suggestions: filters on its id instead of the text
  farmFilterId: number = null;
  private textFilterChanges = new Subject<void>();
  readonly farmLabel = (farm: Farm) => farm.name;
  readonly farmDetail = (farm: Farm) => [farm.postalCode, farm.city].filter(Boolean).join(' ');
  readonly organisationLabel = (organisation: Organisation) => organisation.name;
  readonly searchFarms = (term: string): Observable<Farm[]> =>
    this.farmService
      .getFarmsAdvanced(1, 20, 'name', 'asc', { filter: term }, null, null, null)
      .pipe(map((page) => page.content));
  varietyFilter: number | string = null;
  advisorFilter: number | string = null;

  filterToggleIcon = 'fa fa-plus';
  showAllFilters = false;
  varietyDict: Map<number, Variety>;
  varietys: Variety[];

  parcels: ARColzaParcel[] = [];
  page: number = 1;
  itemsPerPage: number = 25;
  maxSize: number = 5;
  numPages: number = 1;
  length: number = 0;

  campaigns: ARColzaCampaign[];
  numAvailableParcels = 0;

  isAdmin: boolean;
  isNegoce: boolean;
  isManager: boolean;
  canRemoveOrders: boolean;
  showBeApi = false;

  showOrganisationFilter = false;
  organisations: Organisation[];

  tooltipTexts = TOOLTIP_TEXTS;

  // static pour garder entre affichages de la liste
  static filterToApply: ARColzaParcelFilter;
  // What the farm and organisation autocompletes show, kept with filterToApply
  static filterTextsToApply: { farm: string; farmId: number; organisation: string; organisationId: number };
  static allFiltersToShow: boolean;

  constructor(
    private farmService: FarmService,
    private colzaService: ARColzaService,
    private beApiService: BeApiService,
    private organisationService: OrganisationService,
    private listsService: ListsService,
    private i18nTranslator: I18nTranslator,
    private router: Router,
    private route: ActivatedRoute,
    private modal: ModalService,
    private cdr: ChangeDetectorRef,
  ) {
    const authz = KeycloakService.auth.authz;
    this.isAdmin = authz.hasRealmRole('ROLE_ADMIN');
    this.isNegoce = authz.hasRealmRole('ROLE_NEGOCE');
    this.isManager = authz.hasRealmRole('ROLE_MANAGER');
    this.canRemoveOrders = !authz.hasRealmRole('ROLE_FARMER') && !authz.hasRealmRole('ROLE_ADVISOR');
  }

  getParcelsPaginated(pageIndex: number, sortColumnName: string, sortColumnOrder: string, filter: ARColzaParcelFilter) {
    this.colzaService
      .getPageParcels(pageIndex, this.itemsPerPage, sortColumnName, sortColumnOrder, filter)
      .subscribe((page) => this.handlePage(page));
  }

  getNumAvailableParcels() {
    this.colzaService.countOrderItems(true, false).subscribe(
      (count) => {
        this.numAvailableParcels = count;
      },
      (error) => {
        this.handleError(error, this.i18nTranslator.translate('ar.table.error.recupParcellesDispo'));
      },
    );
  }

  handlePage(page: Page<ARColzaParcel>) {
    this.parcels = page.content;
    this.numPages = page.totalPages;
    this.length = page.totalElements;

    this.beApiService.fillBeApiStatus(this.parcels, this.showBeApi);
  }

  ngOnInit() {
    const obs4 = this.getColzaDestination();
    const obs2 = this.getCampaigns();
    const obs3 = this.getOrganisations();

    forkJoin(obs2, obs3, obs4).subscribe(() => {
      const p = this.route.snapshot.params as any;
      const hasRouteParams = p['campaignId'] || p['status'] === 'incomplete';
      // Initialize default campaign filter if no filter exists
      if (!ARColzaParcelComponent.filterToApply || hasRouteParams) {
        if (!this.campaignFilter && this.campaigns && this.campaigns.length) {
          this.campaignFilter = this.campaigns[0].id;
        }

        // Apply route parameters (overrides previous filters)
        this.applyRouteShortcuts();

        // Build filter with all parameters applied (always rebuild)
        ARColzaParcelComponent.filterToApply = this.buildFilter();
      }

      this.updateUIWithFilters();
      this.onChangeTable();
    });
  }

  updateUIWithFilters() {
    const f = ARColzaParcelComponent.filterToApply;
    if (f) {
      if (this.isAdmin || this.isNegoce || this.isManager) {
        this.advisorFilter = f.advisorId;
        if (this.termAdvisorFilter && this.termAdvisorFilter.nativeElement) {
          this.termAdvisorFilter.nativeElement.value = this.advisorFilter || '';
        }
      }
      if (this.isAdmin) {
        this.prefixFilterIdParcel = f.prefixIdParcel;
        if (this.termPrefixFilterIdParcel && this.termPrefixFilterIdParcel.nativeElement) {
          this.termPrefixFilterIdParcel.nativeElement.value = this.prefixFilterIdParcel || '';
        }
      }
      this.prefixFilterParcel = f.prefixParcel;
      if (this.termPrefixFilterParcel && this.termPrefixFilterParcel.nativeElement) {
        this.termPrefixFilterParcel.nativeElement.value = this.prefixFilterParcel || '';
      }
      const texts = ARColzaParcelComponent.filterTextsToApply;
      if (texts) {
        this.prefixFilterExploitation = texts.farm;
        this.farmFilterId = texts.farmId;
        this.organisationFilterText = texts.organisation;
        this.organisationFilter = texts.organisationId;
      }
      this.campaignFilter = toNumber(f.campaignId);
      if (this.termCampaignFilter && this.termCampaignFilter.nativeElement) {
        this.termCampaignFilter.nativeElement.value = this.campaignFilter;
      }
      this.completionStatusFilter = toBoolean(f.completionStatus);
      if (this.termCompletionStatusFilter && this.termCompletionStatusFilter.nativeElement) {
        this.termCompletionStatusFilter.nativeElement.value = this.completionStatusFilter;
      }
      this.orderStatusFilter = toBoolean(f.orderStatus);
      if (this.termOrderStatusFilter && this.termOrderStatusFilter.nativeElement) {
        this.termOrderStatusFilter.nativeElement.value = this.orderStatusFilter;
      }
      this.varietyFilter = toNumber(f.variety);
      if (this.termVarietyFilter && this.termVarietyFilter.nativeElement) {
        this.termVarietyFilter.nativeElement.value = this.varietyFilter;
      }
    }

    if (ARColzaParcelComponent.allFiltersToShow) {
      this.toggleAllFilters();
    }
  }

  ngAfterViewInit() {
    // TODO améliorer => pas de debounce quand clearFilter a été appelé
    const observables = new Array<Observable<{}>>();
    const obs1 = fromEvent(this.termPrefixFilterParcel.nativeElement, 'keyup').pipe(
      debounceTime(300),
      distinctUntilChanged(),
    );
    const obs3 = fromEvent(this.termOrderStatusFilter.nativeElement, 'change');
    const obs4 = fromEvent(this.termCompletionStatusFilter.nativeElement, 'change');
    const obs5 = fromEvent(this.termCampaignFilter.nativeElement, 'change');
    const obs8 = fromEvent(this.termVarietyFilter.nativeElement, 'change');
    observables.push(obs1, this.textFilterChanges.pipe(debounceTime(300), map(() => ({}))), obs3, obs4, obs5, obs8);

    if (this.isAdmin || this.isNegoce || this.isManager) {
      const obs9 = fromEvent(this.termAdvisorFilter.nativeElement, 'keyup').pipe(
        debounceTime(300),
        distinctUntilChanged(),
      );
      observables.push(obs9);
      obs9.subscribe((term) => (this.advisorFilter = <string>this.termAdvisorFilter.nativeElement.value));
    }

    if (this.isAdmin) {
      const obs7 = fromEvent(this.termPrefixFilterIdParcel.nativeElement, 'keyup').pipe(
        debounceTime(300),
        distinctUntilChanged(),
      );
      observables.push(obs7);
      obs7.subscribe((term) => (this.prefixFilterIdParcel = <number>this.termPrefixFilterIdParcel.nativeElement.value));
    }

    obs1.subscribe((term) => (this.prefixFilterParcel = <string>this.termPrefixFilterParcel.nativeElement.value));

    merge(...observables)
      .pipe(debounceTime(10))
      .subscribe((term: any) => {
        this.updateFilters();
      });

    this.colzaService.getCampaigns().subscribe((data: any) => {
      this.handleCampaigns(data);
    });
  }

  getCampaigns(): Observable<ARColzaCampaign[]> {
    const obs = this.colzaService.getCampaigns().pipe(share());
    obs.subscribe((campaigns) => {
      this.campaigns = campaigns.sort(Campaign.compare).reverse();
    });
    return obs;
  }

  getOrganisations(): Observable<Organisation[]> {
    const isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
    const isSector = KeycloakService.auth.authz.hasRealmRole('ROLE_SECTOR');
    const isNegoce = KeycloakService.auth.authz.hasRealmRole('ROLE_NEGOCE');

    if (isAdmin || isNegoce || isSector) {
      this.showOrganisationFilter = true;
    }

    // roles WANAKA
    if (isAdmin || isSector) {
      // pour Wanaka : on affiche la colonne BeApi toujours
      this.showBeApi = true;
    }

    // FIXME quand i18n est mergé : récupérer l'organisation de l'utilisateur depuis la structure i18n et décider si on affiche la colonne BeApi selon
    // pour l'instant on récupére l'organisation de l'utilisateur depuis le service
    const obs = this.organisationService.getOrganisations().pipe(share());
    obs.subscribe((orgs) => {
      this.organisations = orgs.sort((a, b) => a.name.localeCompare(b.name));
      if (!(isAdmin || isSector)) {
        // utilisateur normal : 1 seule organisation visible
        // TODO pas pour négoce ? plusieurs
        const organisation = this.organisations[0];
        this.showBeApi = organisation.hasBeApi;
      }
    });

    return obs;
  }

  clearFilters() {
    this.campaignFilter = currentCampaign(this.campaigns);

    this.prefixFilterParcel = null;
    this.prefixFilterExploitation = null;
    this.prefixFilterIdParcel = null;
    this.completionStatusFilter = null;
    this.orderStatusFilter = null;
    this.varietyFilter = null;
    this.advisorFilter = null;
    this.organisationFilter = null;
    this.organisationFilterText = '';
    this.farmFilterId = null;

    this.termPrefixFilterParcel.nativeElement.value = null;
    this.termCompletionStatusFilter.nativeElement.value = null;
    this.termOrderStatusFilter.nativeElement.value = null;
    this.termVarietyFilter.nativeElement.value = null;

    if (this.termAdvisorFilter) {
      this.termAdvisorFilter.nativeElement.value = null;
    }
    if (this.termPrefixFilterIdParcel) {
      this.termPrefixFilterIdParcel.nativeElement.value = null;
    }
    this.updateFilters();
  }

  updateFilters() {
    ARColzaParcelComponent.filterToApply = this.buildFilter();
    ARColzaParcelComponent.filterTextsToApply = {
      farm: this.prefixFilterExploitation,
      farmId: this.farmFilterId,
      organisation: this.organisationFilterText,
      organisationId: toNull(this.organisationFilter),
    };

    // reset la page à la première et update la table
    // FIXME faire mieux : suspendre le traitement des changements ?
    if (this.page === 1) {
      this.onChangeTable();
    } else {
      // le biding (pagechanged) du contrôle de pagination va appeler onChangeTable
      this.page = 1;
    }

    this.clearCheckboxes();
  }

  onChangeTable(page: any = { page: this.page, itemsPerPage: this.itemsPerPage }): any {
    const pageNumber = page.page;
    this.getParcelsPaginated(pageNumber, null, null, ARColzaParcelComponent.filterToApply);
    this.getNumAvailableParcels();
  }

  onChangeItemsPerPage(newValue: number) {
    const page = this.page;
    this.cdr.detectChanges();
    if (page === this.page) {
      this.onChangeTable();
    }
  }

  buildFilter(): ARColzaParcelFilter {
    // binding Angular2 écrit en chaine... On fait la conversion en boolean
    const compFilter: string = '' + this.completionStatusFilter;
    const stFilter: string = '' + this.orderStatusFilter;
    const filter = {
      prefixParcel: toNull(this.prefixFilterParcel),
      // A picked farm filters on its id; typed text on the name
      prefixExploitation: this.farmFilterId ? null : this.prefixFilterExploitation?.trim() || null,
      farmId: this.farmFilterId,
      prefixIdParcel: toNumber(this.prefixFilterIdParcel),
      campaignId: toNull(this.campaignFilter),
      // Organisations are filtered by id only: typed text becomes the ids it matches
      organisationId:
        toNull(this.organisationFilter) ??
        idsMatchingText(this.organisations, this.organisationFilterText, this.organisationLabel, (o) => o.id),
      completionStatus: compFilter === 'true' ? true : compFilter === 'false' ? false : null,
      orderStatus: stFilter === 'true' ? true : stFilter === 'false' ? false : null,
      variety: toNull(this.varietyFilter),
      advisorId: toNull(this.advisorFilter),
    };
    return filter;
  }

  addOrderItem(parcel: ARColzaParcel) {
    this.router.navigate(['/orders', 'azote', 'colza', 'parcels', 'new', parcel.id]);
  }

  deleteOrderItem(orderItem: ARColzaOrderItem, event: any) {
    event.stopPropagation();
    // FIXME traiter les erreurs
    this.colzaService.deleteOrderItem(orderItem).subscribe(
      (res) => {
        this.onChangeTable();
      },
      (error) => this.handleError(error, this.i18nTranslator.translate('ar.colza.error.suppr')),
    );
  }

  editOrderItem(orderItem: ARColzaOrderItem, event: any) {
    this.router.navigate(['/orders', 'azote', 'colza', 'parcels', 'edit', orderItem.id]);
  }

  handleError(error: any, msg: string) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('ar.table.error.unauthorized'));
    } else {
      console.error(error);
      showErrorMessage(this.modal, msg);
    }
  }

  viewParcel(parcelId: number, event: any) {
    const context = new ParcelDetailsDialogContext();
    context.showClose = true;
    context.isBlocking = false;
    context.size = 'lg';
    context.parcelId = parcelId;
    context.showEdit = true;
    this.modal.open(ParcelDetailsDialogComponent, context);
  }

  isCampaignCurrentlyOpen(campaign: ARColzaCampaign): boolean {
    return ARColzaUtils.isCampaignCurrentlyOpen(campaign);
  }

  addOrder() {
    if (this.checkedParcelIds.size === 0) {
      this.router.navigate(['/orders/azote/colza/orders/new']);
    } else {
      const parcelIds = Array.from(this.checkedParcelIds.values()).join('-');
      this.router.navigate(['/orders/azote/colza/orders/new/' + parcelIds]);
    }
  }

  batchEdit() {
    if (this.checkedParcelIds.size > 1) {
      const parcelIds = Array.from(this.checkedParcelIds.values()).join('-');
      this.router.navigate(['/orders/azote/colza/parcels/batch/' + parcelIds]);
    } else if (this.checkedParcelIds.size === 1) {
      // on redirige vers la page non batch (sans les checkbox par donnée)
      const parcelId = Array.from(this.checkedParcelIds.values())[0];
      this.router.navigate(['/orders/azote/colza/parcels/edit/' + parcelId]);
    }
  }

  removeOrders() {
    const selectedParcels = this.parcels.filter((parcel) => this.checkedParcelIds.has(parcel.id));

    if (!selectedParcels.length) {
      return;
    }

    const parcelsWithOrders = selectedParcels.filter((parcel) => !!parcel.orderItem);
    const parcelsWithoutOrders = selectedParcels.filter((parcel) => !parcel.orderItem).map((parcel) => parcel.id);
    const parcelIdsToDelete = parcelsWithOrders.map((parcel) => parcel.id);

    if (parcelIdsToDelete.length === 0) {
      const summary = this.createSummaryForParcelsWithoutOrders(parcelsWithoutOrders);
      if (summary) {
        this.showDeletionSummary(summary);
      }
      return;
    }

    this.colzaService.deleteParcelOrderItems(parcelIdsToDelete).subscribe(
      (summary: OrderItemDeletionSummary) => {
        this.clearCheckboxes();
        this.onChangeTable();
        const finalSummary = this.appendParcelsWithoutOrdersToSummary(summary, parcelsWithoutOrders);
        this.showDeletionSummary(finalSummary);
      },
      (error) => {
        const fallbackMessage = this.i18nTranslator.translate('ar.colza.error.suppr');
        const errorMessage = extractErrorMessage(error, fallbackMessage);
        showErrorMessage(this.modal, errorMessage);
        this.onChangeTable();
      },
    );
  }

  private createSummaryForParcelsWithoutOrders(parcelIds: number[]): OrderItemDeletionSummary {
    const failure = this.buildParcelWithoutOrderFailure(parcelIds);
    if (!failure) {
      return null;
    }

    const failureCount = failure.count || parcelIds.length;

    return {
      failures: [failure],
      failureCount,
      requestedCount: parcelIds.length,
      deletedCount: 0,
      errorKey: 'order.delete.error.summary',
      errorArgs: { count: failureCount },
    };
  }

  private appendParcelsWithoutOrdersToSummary(
    summary: OrderItemDeletionSummary,
    parcelIds: number[],
  ): OrderItemDeletionSummary {
    if (!parcelIds || !parcelIds.length) {
      return summary;
    }

    const failure = this.buildParcelWithoutOrderFailure(parcelIds);
    if (!failure) {
      return summary;
    }

    let baseSummary: OrderItemDeletionSummary;
    if (summary) {
      baseSummary = {
        failures: summary.failures ? summary.failures.slice() : [],
        requestedCount: typeof summary.requestedCount === 'number' ? summary.requestedCount : 0,
        deletedCount: typeof summary.deletedCount === 'number' ? summary.deletedCount : 0,
        failureCount: typeof summary.failureCount === 'number' ? summary.failureCount : 0,
        errorArgs: summary.errorArgs ? Object.assign({}, summary.errorArgs) : {},
      };
      if (summary.errorKey) {
        baseSummary.errorKey = summary.errorKey;
      }
      if (summary.error) {
        baseSummary.error = summary.error;
      }
    } else {
      baseSummary = {
        failures: [],
        requestedCount: 0,
        deletedCount: 0,
        failureCount: 0,
        errorArgs: {},
      };
    }

    baseSummary.failures.push(failure);
    const failureIncrement = failure.count || parcelIds.length;
    const existingFailureCount = typeof baseSummary.failureCount === 'number' ? baseSummary.failureCount : 0;
    baseSummary.failureCount = existingFailureCount + failureIncrement;
    const existingRequestedCount = typeof baseSummary.requestedCount === 'number' ? baseSummary.requestedCount : 0;
    baseSummary.requestedCount = existingRequestedCount + parcelIds.length;

    if (!baseSummary.errorKey || baseSummary.errorKey === 'order.delete.error.summary') {
      baseSummary.errorKey = 'order.delete.error.summary';
      baseSummary.errorArgs = { count: baseSummary.failureCount };
    }

    return baseSummary;
  }

  private buildParcelWithoutOrderFailure(parcelIds: number[]): OrderItemDeletionFailure {
    if (!parcelIds || !parcelIds.length) {
      return null;
    }

    return {
      messageKey: 'order.delete.error.noOrderItem',
      count: parcelIds.length,
      parcelIds: parcelIds.slice(),
    };
  }

  private showDeletionSummary(summary: OrderItemDeletionSummary) {
    if (!summary) {
      return;
    }

    const messages: { text: string; isError: boolean }[] = [];
    const deletedCount = typeof summary.deletedCount === 'number' ? summary.deletedCount : 0;
    const failureCount = typeof summary.failureCount === 'number' ? summary.failureCount : 0;
    const hasFailures = failureCount > 0 || (summary.failures && summary.failures.length > 0);

    if (deletedCount > 0) {
      const successText = this.translateDeletionMessage('order.delete.success.summary', { count: deletedCount }, null);
      if (successText) {
        messages.push({ text: successText, isError: false });
      }
    }

    if (hasFailures) {
      const summaryText = this.translateDeletionMessage(summary.errorKey, summary.errorArgs, summary.error);
      if (summaryText) {
        messages.push({ text: summaryText, isError: true });
      }

      if (summary.failures && summary.failures.length) {
        for (const failure of summary.failures) {
          const failureText = this.translateDeletionMessage(
            failure.messageKey,
            failure.messageArgs,
            failure.defaultMessage,
          );
          let messageLine = failureText || '';
          const detailParts: string[] = [];
          if (failure.count !== undefined && failure.count !== null) {
            const parcelsLabel = this.i18nTranslator.translate('planning.error.parcels');
            detailParts.push(`${failure.count} ${parcelsLabel}`);
          }

          if (detailParts.length) {
            messageLine += ` (${detailParts.join(' - ')})`;
          }

          if (messageLine) {
            messages.push({ text: messageLine, isError: true });
          }
        }
      }
    }

    if (!messages.length) {
      return;
    }

    const title = this.i18nTranslator.translate('order.delete.error.dialogTitle');
    this.openDeletionModal(title, messages);
  }

  private translateDeletionMessage(key: string, args: any, fallback: string) {
    if (key) {
      const translation = this.i18nTranslator.translate(key, args);
      if (translation && translation !== key) {
        return translation;
      }
    }

    if (fallback) {
      return fallback;
    }

    return key || '';
  }

  private openDeletionModal(title: string, messages: { text: string; isError: boolean }[]) {
    const context = new StatusModalContext();
    Object.assign(context, {
      title,
      messages,
      dialogClass: 'modal-dialog',
      size: 'lg',
      showClose: true,
      keyboard: true,
      blocking: false,
      closeByClickingOutside: false,
    });

    this.modal.open(CustomStatusModalComponent, context).then((dialogRef) => {
      dialogRef.result.catch(() => {
        console.log('User closed status modal');
      });
    });
  }

  addParcel() {
    this.router.navigate(['/parcels', 'new']);
  }

  isParcelChecked(parcel: ARColzaParcel): boolean {
    return this.checkedParcelIds.has(parcel.id);
  }

  onCheckboxClicked(index: number) {
    const checkboxes = this.parcelCheckboxes.toArray().map((x: any) => x.nativeElement);
    const parcel = this.parcels[index];
    const checkbox = checkboxes[index];

    if (checkbox.checked) {
      this.checkedParcelIds.add(parcel.id);
    } else {
      this.checkedParcelIds.delete(parcel.id);
    }
  }

  onCheckboxAllClicked() {
    if (this.checkboxAll.nativeElement.checked) {
      const filter = this.buildFilter();
      this.colzaService.getAllParcelIds(filter).subscribe(
        (x) => {
          this.lengthAllParcels = x.length;

          for (const idx of x) {
            this.checkedParcelIds.add(idx);
          }
          // pas de modèle : on coche à la main
          const checkboxes = this.parcelCheckboxes.toArray().map((x: any) => x.nativeElement);
          for (let i = 0; i < checkboxes.length; i++) {
            const checkbox = checkboxes[i];
            checkbox.checked = true;
          }
        },
        (error) => {
          this.handleError(error, this.i18nTranslator.translate('ar.table.error.selection'));
        },
      );
    } else {
      this.clearCheckboxes();
    }
  }

  clearCheckboxes() {
    this.lengthAllParcels = undefined;
    this.checkboxAll.nativeElement.checked = false;
    // decoche tout
    this.checkedParcelIds.clear();
    // pas de modèle : on décoche à la main
    const checkboxes = this.parcelCheckboxes.toArray().map((x: any) => x.nativeElement);
    for (const checkbox of checkboxes) {
      checkbox.checked = false;
    }
  }

  isCheckboxAllChecked(): boolean {
    return this.lengthAllParcels === this.checkedParcelIds.size;
  }

  handleCampaigns(campaigns: ARColzaCampaign[]) {
    this.campaigns = campaigns.sort(Campaign.compare).reverse();
    campaigns = campaigns.filter((x) => ARColzaUtils.isCampaignCurrentlyOpen(x));
    for (const campaign of campaigns) {
      const dateChecks = {
        campaign: campaign,
      };
      this.openedCampaignsDateChecks.set(campaign.id, dateChecks);
    }
  }

  toggleAllFilters() {
    if (this.showAllFilters) {
      this.showAllFilters = false;
      this.filterToggleIcon = 'fa fa-plus';
      this.allFilters.nativeElement.style.display = 'none';
    } else {
      this.showAllFilters = true;
      this.filterToggleIcon = 'fa fa-minus';
      this.allFilters.nativeElement.style.display = 'block';
    }

    ARColzaParcelComponent.allFiltersToShow = this.showAllFilters;
  }

  getColzaDestination(): Observable<Map<ListType, ListItem[]>> {
    const obs = this.listsService.getListTypes(ListType, ListType.VARIETIES).pipe(share());
    obs.subscribe(
      (lists: Map<ListType, Variety[]>) => {
        const variety = lists.get(ListType.VARIETIES);
        const correctVariety: any[] = [];
        for (const vari of variety) {
          if (vari.specy.code.includes('COLZA')) {
            correctVariety.push(vari);
          }
        }
        this.varietys = correctVariety.sort((a, b) => a.label.localeCompare(b.label));
      },
      (err) => this.handleError(err, this.i18nTranslator.translate('ar.colza.error.varietes')),
    );
    return obs;
  }

  private applyRouteShortcuts(): void {
    const p = this.route.snapshot.params as any;

    const routeCampaignId = +p['campaignId'];
    if (routeCampaignId) {
      this.campaignFilter = routeCampaignId;
    }

    if (p['status'] === 'incomplete' && +p['onlyIncomplete'] === 1) {
      this.completionStatusFilter = false;
      //this.orderStatusFilter = true;
    }
  }

  onFarmTextChange(text: string) {
    this.prefixFilterExploitation = text;
    this.farmFilterId = null;
    this.textFilterChanges.next();
  }

  onFarmSelected(farm: Farm) {
    this.prefixFilterExploitation = farm.name;
    this.farmFilterId = farm.id;
    this.updateFilters();
  }

  onOrganisationTextChange(text: string) {
    this.organisationFilterText = text;
    this.organisationFilter = null;
    this.textFilterChanges.next();
  }

  onOrganisationSelected(organisation: Organisation) {
    this.organisationFilterText = organisation.name;
    this.organisationFilter = organisation.id;
    this.updateFilters();
  }
}
